"use client";

// Review — a staged move-in, looked at before anything is kept
// (MOVING-IN-PLAN.md §13, F.moveIn).
//
// John, 2026-10-24: "show and visualize exactly all the different changes …
// confirm all the changes, unselect any that you don't want, or edit them right
// on the fly before they all get written into the memory … nothing overwhelming."
//
// Every change netti would make, sorted by where it would go: about you (by the
// part of you it is about) · people in your life (by who) · your projects (by
// project, a new one said so) · how your ais should work (by the ai it is for) ·
// netti's questions. Each item is kept unless the person unselects it; its words
// can be changed in place; "where it came from" opens the exact span it rests on
// in THE code block, and "your words" marks the ones that are the person's own.
// What netti noticed across the pile sits above, in a few lines.
//
// Nothing here writes. The person's choices are kept as they make them
// (lib/os/resume), so leaving and coming back finds every tick and every edit;
// the plan itself lives on its run, so another device finds it too. One press
// (the sheet's action slot) keeps what is kept.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CopyBlock from "../CopyBlock";
import FilterLine from "../activity/FilterLine";
import { useResumable, forgetResumable } from "../../lib/os/resume";
import type { IntakeItem, ItemKind } from "../../lib/intake/items";
import { KIND_WORDS, type SourceKind } from "../../lib/intake/sources";
import { TOOL_WORDS } from "../../lib/intake/readFiles";
import { PART_WORDS } from "./words";
import { MOVED_CHANGED_EVENT, movedChanged } from "../../lib/intake/open";
import "./review.css";

export interface PagePlan {
  sources: Array<{ title: string; kind: SourceKind; for?: string; size: number }>;
  items: IntakeItem[];
  noticed: string[];
  summary: string;
  failed: number;
  /** parts netti is still reading on its own (163): the plan grows when they are in */
  reading?: number;
}

type Choice = { keep?: boolean; text?: string };
type Choices = Record<string, Choice>;

const SECTIONS: Array<{ id: Section; label: string; kinds: ItemKind[] }> = [
  { id: "you", label: "about you", kinds: ["fact"] },
  { id: "people", label: "people in your life", kinds: ["other"] },
  { id: "projects", label: "your projects", kinds: ["note"] },
  { id: "ais", label: "how your ais should work", kinds: ["line"] },
  { id: "questions", label: "netti's questions", kinds: ["question"] },
];
type Section = "you" | "people" | "projects" | "ais" | "questions";

const CLASS_WORDS: Record<string, string> = {
  private: "private",
  self: "just you",
  trusted: "people you trust",
  work: "work",
  public: "public",
};
const FIELD_WORDS: Record<string, string> = { talks: "how it talks", answers: "how it answers", behaves: "how it behaves", habit: "a habit" };

/** the label before the relation ("ravi, mondeep's mother" → "ravi") */
const whoOf = (about: string | null | undefined) => {
  const s = (about ?? "").replace(/^\u0000/, "").trim();
  return s ? s.split(/\s*[,(]/)[0] : "someone in your life";
};

/** the group an item sits in, inside its section */
export function groupOf(it: IntakeItem): string {
  switch (it.kind) {
    case "fact":
      return PART_WORDS[it.dimension ?? ""] ?? (it.dimension ?? "about you").replace(/_/g, " ");
    case "other":
      return whoOf(it.about);
    case "note":
      return `${it.project?.name ?? "a project"}${it.project?.new ? " · new" : ""}`;
    case "line":
      return TOOL_WORDS[(it.identity?.for ?? "") as keyof typeof TOOL_WORDS] ?? it.identity?.for ?? "every ai";
    default:
      return "";
  }
}

/** where a kept item would go, in a few words */
function whereOf(it: IntakeItem): string {
  switch (it.kind) {
    case "fact":
      return CLASS_WORDS[it.disclosure ?? "self"] ?? "just you";
    case "other":
      return "kept as theirs";
    case "note":
      return it.project?.new ? "into a new project" : "into this project";
    case "line":
      return FIELD_WORDS[it.identity?.field ?? "habit"] ?? "a habit";
    default:
      return "for you to answer";
  }
}

export interface ReviewState {
  /** keeping: a press is writing it now · error: it could not be read just now (try again) */
  plan: PagePlan | "loading" | "missing" | "decided" | "keeping" | "error";
  items: IntakeItem[];
  kept: number;
  total: number;
  choose: (id: string, c: Choice) => void;
  chooseAll: (ids: string[], keep: boolean) => void;
  apply: () => Promise<boolean>;
  discard: () => Promise<boolean>;
  /** read the plan again */
  retry: () => void;
  busy: boolean;
  error: string;
}

/** the plan read, tried again on its own before the sheet says anything */
const READ_RETRIES = [3_000, 8_000, 20_000];
/** a keep the server could not take yet, pressed again on its own */
const KEEP_RETRIES = [2_000, 5_000, 12_000, 30_000];

interface RunView {
  plan: ReviewState["plan"];
  busy: boolean;
  error: string;
  /** reads of the plan so far: a re-read keeps what is shown until the answer is in */
  round: number;
}
const FRESH: RunView = { plan: "loading", busy: false, error: "", round: 0 };

/**
 * One staged move-in, and the person's choices on it (kept as they make them).
 * Everything shown belongs to one run: another run starts fresh, and an answer that
 * arrives for a run no longer shown is dropped.
 */
export function useReview(runId: string | null): ReviewState {
  const [view, setView] = useState<{ run: string | null } & RunView>({ run: null, ...FRESH });
  const [choices, setChoices, forgetChoices] = useResumable<Choices>(runId ? `intake-review:${runId}` : null, {});
  const shown = useRef(runId);
  useEffect(() => {
    shown.current = runId;
  }, [runId]);
  const mine: RunView = view.run === runId ? view : FRESH;
  const { plan, busy, error, round } = mine;
  // a write lands on its own run while that run is the one held (or shown); one for a run long gone is dropped
  const patch = useCallback((run: string, p: Partial<RunView> | ((was: RunView) => Partial<RunView>)) => {
    setView((v) => {
      if (v.run !== run && shown.current !== run) return v;
      const was: RunView = v.run === run ? v : FRESH;
      return { ...was, ...(typeof p === "function" ? p(was) : p), run };
    });
  }, []);

  // the plan as it stands on the server: after a press that kept only part of it, just what is still waiting.
  // a read that fails is tried again on its own (3 s, 8 s, 20 s) before the sheet says anything at all
  useEffect(() => {
    if (!runId) return;
    const run = runId;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const quietly = (tries: number) => {
      if (tries < READ_RETRIES.length) timer = setTimeout(() => read(tries + 1), READ_RETRIES[tries]);
      else patch(run, (was) => ({ plan: typeof was.plan === "object" ? was.plan : "error" }));
    };
    const read = (tries: number) =>
      void fetch(`/api/intake/stage?id=${encodeURIComponent(run)}`, { cache: "no-store" })
        .then(async (r) => {
          if (!alive) return;
          if (r.status === 404) return patch(run, { plan: "missing" });
          if (!r.ok) return quietly(tries);
          const j = (await r.json().catch(() => null)) as { plan?: PagePlan | null; run?: { stage?: string | null; keep_at?: string | null } | null } | null;
          if (!j) return quietly(tries);
          const stage = j.run?.stage ?? null;
          // the person already pressed keep: netti is writing it, whatever the plan still holds
          if (j.run?.keep_at && (stage === "staged" || stage === "applying")) return patch(run, { plan: "keeping" });
          // no plan: a press is keeping it, or it waits but would not open just now (read again), or it was decided
          if (!j.plan && stage === "staged") return quietly(tries);
          patch(run, { plan: j.plan ? j.plan : stage === "applying" ? "keeping" : "decided" });
        })
        .catch(() => alive && quietly(tries));
    read(0);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [runId, round, patch]);

  // read again when this run is kept, let go or taken back anywhere; and every few seconds while a press is keeping it
  useEffect(() => {
    if (!runId) return;
    const run = runId;
    const again = (e: Event) => {
      if ((e as CustomEvent<{ run?: string }>).detail?.run === run) patch(run, (was) => ({ round: was.round + 1 }));
    };
    window.addEventListener(MOVED_CHANGED_EVENT, again);
    return () => window.removeEventListener(MOVED_CHANGED_EVENT, again);
  }, [runId, patch]);
  useEffect(() => {
    // parts netti is still reading: the plan is read again every 20 s while the sheet is open, so it grows in place
    if (!runId || typeof plan !== "object" || !plan.reading || round > 90) return;
    const run = runId;
    const t = window.setTimeout(() => patch(run, (was) => ({ round: was.round + 1 })), 20_000);
    return () => window.clearTimeout(t);
  }, [runId, plan, round, patch]);
  useEffect(() => {
    if (!runId || plan !== "keeping" || round > 60) return;
    const run = runId;
    const t = window.setTimeout(() => patch(run, (was) => ({ round: was.round + 1 })), 5000);
    return () => window.clearTimeout(t);
  }, [runId, plan, round, patch]);

  const items = useMemo(
    () =>
      typeof plan === "object"
        ? plan.items.map((it) => {
            const c = choices[it.id];
            return c ? { ...it, keep: c.keep ?? it.keep, ...(c.text != null && c.text !== it.text ? { text: c.text, edited: true } : {}) } : it;
          })
        : [],
    [plan, choices],
  );

  const choose = useCallback((id: string, c: Choice) => setChoices((was) => ({ ...was, [id]: { ...was[id], ...c } })), [setChoices]);
  const chooseAll = useCallback(
    (ids: string[], keep: boolean) =>
      setChoices((was) => {
        const next = { ...was };
        for (const id of ids) next[id] = { ...next[id], keep };
        return next;
      }),
    [setChoices],
  );
  const retry = useCallback(() => {
    if (runId) patch(runId, (was) => ({ round: was.round + 1, ...(was.plan === "error" ? { plan: "loading" as const } : {}) }));
  }, [runId, patch]);

  const apply = useCallback(async () => {
    if (!runId || typeof plan !== "object") return false;
    const run = runId;
    patch(run, { busy: true, error: "" });
    const body = JSON.stringify({ run, decisions: items.map((i) => ({ id: i.id, keep: i.keep, ...(i.edited ? { text: i.text } : {}) })) });
    try {
      // the press is pressed again on its own while the server cannot take it yet (a blip, a plan that would
      // not open, a dropped line); the person pressed once, and that is all they do (MOVING-IN-PLAN §16)
      for (let tries = 0; ; tries++) {
        let r: Response | null = null;
        try {
          r = await fetch("/api/intake/apply", { method: "POST", headers: { "content-type": "application/json" }, body });
        } catch {
          r = null;
        }
        const j = r ? ((await r.json().catch(() => ({}))) as { error?: string }) : {};
        if (r?.ok) {
          // kept, or being kept by netti (keeping): either way it is no longer the person's to do
          if (shown.current === run) forgetChoices();
          else forgetReview(run);
          movedChanged(run);
          return true;
        }
        if (r?.status === 404) {
          patch(run, { error: j.error ?? "this move-in is not here any more." });
          return false;
        }
        if (tries >= KEEP_RETRIES.length) {
          // still not through after a while: said quietly, and the press stays one tap away
          patch(run, { error: "innernet could not be reached just now. what you chose is kept here; it goes in when you press again." });
          return false;
        }
        await new Promise((ok) => setTimeout(ok, KEEP_RETRIES[tries]));
      }
    } finally {
      patch(run, { busy: false });
    }
  }, [runId, plan, items, forgetChoices, patch]);

  const discard = useCallback(async () => {
    if (!runId) return false;
    const run = runId;
    patch(run, { busy: true });
    try {
      const r = await fetch(`/api/intake/stage?id=${encodeURIComponent(run)}`, { method: "DELETE" });
      const j = (await r.json().catch(() => ({}))) as { ok?: boolean };
      const ok = r.ok && j.ok !== false;
      if (ok) {
        if (shown.current === run) forgetChoices();
        else forgetReview(run);
        movedChanged(run);
      }
      return ok;
    } catch {
      return false;
    } finally {
      patch(run, { busy: false });
    }
  }, [runId, forgetChoices, patch]);

  return { plan, items, kept: items.filter((i) => i.keep).length, total: items.length, choose, chooseAll, apply, discard, retry, busy, error };
}

function Check() {
  return (
    <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M3 7.4 5.8 10.1 11 4.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Row({ it, source, onChoose }: { it: IntakeItem; source: string; onChoose: (c: Choice) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(it.text);
  const [why, setWhy] = useState(false);
  const save = () => {
    const t = draft.replace(/\s+/g, " ").trim();
    if (t && t !== it.text) onChoose({ text: t });
    setEditing(false);
  };
  return (
    <li className="rv-item" data-off={!it.keep || undefined}>
      <button
        type="button"
        role="checkbox"
        aria-checked={it.keep}
        aria-label={it.keep ? "kept. press to let it go" : "let go. press to keep it"}
        className="icon-circle icon-circle--sm rv-keep"
        data-on={it.keep || undefined}
        onClick={() => onChoose({ keep: !it.keep })}
      >
        {it.keep ? <Check /> : null}
      </button>
      <div className="rv-body">
        {editing ? (
          <textarea
            className="input-soft rv-edit"
            value={draft}
            autoFocus
            rows={Math.min(6, Math.max(2, Math.ceil(draft.length / 70)))}
            aria-label="change its words"
            onChange={(e) => setDraft(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                save();
              }
              if (e.key === "Escape") {
                setDraft(it.text);
                setEditing(false);
              }
            }}
          />
        ) : (
          <p className="rv-text">{it.text}</p>
        )}
        <div className="rv-meta">
          {it.voice === "said" && <span className="rv-said">your words</span>}
          {it.edited && <span className="rv-said">changed by you</span>}
          <span>{whereOf(it)}</span>
          {source && <span className="rv-from">from {source}</span>}
          {!editing && (
            <button type="button" className="quiet-link" onClick={() => { setDraft(it.text); setEditing(true); }}>
              change
            </button>
          )}
          {it.verbatim ? (
            <button type="button" className="quiet-link" aria-expanded={why} onClick={() => setWhy((v) => !v)}>
              {why ? "fold it away" : "where it came from"}
            </button>
          ) : it.voice === "inferred" ? (
            <span className="rv-from">netti&apos;s reading</span>
          ) : null}
        </div>
        {why && it.verbatim && <CopyBlock text={it.verbatim} className="rv-why copy-block--soft" />}
      </div>
    </li>
  );
}

/** The review's body: what netti noticed, then every item by where it would go. */
export function ReviewBody({ review }: { review: ReviewState }) {
  const [onlyRaw, setOnly] = useState<Section | null>(null);
  const { plan, items } = review;
  if (plan === "loading") {
    return (
      <div className="rv" aria-busy="true">
        <span className="skeleton-line" style={{ width: "70%" }} />
        <span className="skeleton-line" style={{ width: "54%" }} />
        <span className="skeleton-line" style={{ width: "62%" }} />
      </div>
    );
  }
  if (plan === "missing") return <p className="cm-sub">this move-in is not here any more.</p>;
  if (plan === "decided") return <p className="cm-sub">this move-in was already decided. what was kept is in your memory.</p>;
  if (plan === "keeping") return <p className="cm-sub">netti is keeping it now. it will be in your memory in a moment.</p>;
  if (plan === "error") {
    return (
      <p className="cm-sub">
        it could not be read just now. nothing was lost.{" "}
        <button type="button" className="quiet-link" onClick={review.retry}>
          please try again
        </button>
      </p>
    );
  }

  const sections = SECTIONS.map((s) => ({ ...s, items: items.filter((i) => s.kinds.includes(i.kind)) })).filter((s) => s.items.length);
  // a filter on a section that is gone (a press kept that part) shows everything again
  const only = onlyRaw && sections.some((s) => s.id === onlyRaw) ? onlyRaw : null;
  const shown = only ? sections.filter((s) => s.id === only) : sections;
  const title = (i: IntakeItem) => plan.sources[i.source]?.title ?? "";

  return (
    <div className="rv">
      {plan.summary && <p className="cm-sub rv-summary">{plan.summary}</p>}
      {plan.noticed.length > 0 && (
        <div className="rv-noticed">
          <span className="rv-noticed__label">netti noticed</span>
          <ul>
            {plan.noticed.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </div>
      )}
      {sections.length > 1 && (
        // one quiet filter line, never a row of chips (John, 2026-10-25)
        <div className="rv-filter">
          <FilterLine
            options={sections.map((x) => ({ key: x.id, label: x.label, count: x.items.length }))}
            total={items.length}
            on={only}
            onChange={(k) => setOnly(k as Section | null)}
          />
        </div>
      )}
      {shown.map((s) => {
        const groups = new Map<string, IntakeItem[]>();
        for (const it of s.items) {
          const g = groupOf(it);
          groups.set(g, [...(groups.get(g) ?? []), it]);
        }
        const kept = s.items.filter((i) => i.keep).length;
        return (
          <section key={s.id} className="rv-sec" aria-label={s.label}>
            <div className="rv-sec__head">
              <h3 className="rv-sec__title">{s.label}</h3>
              <span className="rv-sec__count">
                {kept === s.items.length ? `all ${s.items.length} kept` : `${kept} of ${s.items.length} kept`}
              </span>
              <button type="button" className="quiet-link" onClick={() => review.chooseAll(s.items.map((i) => i.id), kept < s.items.length)}>
                {kept < s.items.length ? "keep all" : "let all go"}
              </button>
            </div>
            {[...groups.entries()].map(([g, list]) => (
              <div key={g} className="rv-group">
                {g && <p className="rv-group__name">{g}</p>}
                <ul className="rv-list">
                  {list.map((it) => (
                    <Row key={it.id} it={it} source={title(it)} onChoose={(c) => review.choose(it.id, c)} />
                  ))}
                </ul>
              </div>
            ))}
          </section>
        );
      })}
      {(plan.reading ?? 0) > 0 && (
        <p className="cm-where rv-reading" aria-live="polite">
          <span aria-hidden>↳</span> netti is still reading {plan.reading === 1 ? "one part" : `${plan.reading} parts`}. {plan.reading === 1 ? "it joins" : "they join"} this look on {plan.reading === 1 ? "its" : "their"} own, or, if you keep this first, {plan.reading === 1 ? "waits" : "wait"} for a look of {plan.reading === 1 ? "its" : "their"} own.
        </p>
      )}
      <div className="rv-sources">
        <span className="rv-noticed__label">what it read</span>
        <span>
          {plan.sources
            .slice(0, 12)
            .map((s) => `${s.title} (${KIND_WORDS[s.kind] ?? s.kind})`)
            .join(" · ")}
          {plan.sources.length > 12 ? ` · and ${plan.sources.length - 12} more` : ""}
        </span>
      </div>
      {review.error && <p className="im-quiet" role="status">{review.error}</p>}
    </div>
  );
}

/** forget a review's kept choices (a run decided elsewhere) */
export const forgetReview = (runId: string) => forgetResumable(`intake-review:${runId}`);
